import { PageContainer, Text, VStack } from '@/components/Atoms'
import { CustomerId, MutedCustomerId } from '@/components/DataTable/CellContent/CustomerId'
import { CustomerNameAddress } from '@/components/DataTable/CellContent/CustomerNameAddress'
import NoDataAvailable from '@/components/DataTable/CellContent/NoDataAvailable'
import { TableHeader } from '@/components/DataTable/CellContent/TableHeader'
import { useCrtCurrencySync } from '@/hooks/crt/useCrtCurrencySync'
import {
  resolveComparison,
  useCustomerVariance,
  type CustomerVariance,
} from '@/hooks/queries/crt/useVarianceAnalysis'
import { VARIANCE_GROUPING_DIMENSION_TYPES } from '@repo/constants'
import { createFileRoute } from '@tanstack/react-router'
import { type ColumnDef } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { CRT_ROUTES, toStaticData } from '../crt-route-config'
import { DrillDataTable } from './-components/DrillDataTable'
import { DrillPageHeader } from './-components/DrillPageHeader'
import { DrillRowActions, type DrillTarget } from './-components/DrillRowActions'
import { drillRoutesFor, TARGET_LABEL_KEYS, type DrillRow } from './-data/drill-dimensions'
import {
  buildRowDrillSearch,
  drillSearchSchema,
  requireComparisonSides,
  toCostTypeCellScope,
  toDrillFilters,
} from './-data/drill-search'

export const Route = createFileRoute('/_auth/_modules/crt/variance-analysis/by-banner')({
  staticData: toStaticData(CRT_ROUTES['/crt/variance-analysis/by-banner']),
  component: RouteComponent,
  validateSearch: (search) => drillSearchSchema.parse(search),
  beforeLoad: ({ search }) => requireComparisonSides(search),
})

/** One row's grouping, as the endpoint sends it. */
type CustomerRowGrouping = CustomerVariance['results'][number]['grouping']

/**
 * The account's display fields, lifted off the grouping so the row carries them beside the dimensions
 * rather than inside them.
 *
 * They are display data, not drill dimensions: no step of the chain narrows by a name or an address, and
 * a `grouping` entry is typed `string | null` precisely because it doubles as a URL search param.
 * Keeping them in their own field is what stops `buildRowDrillSearch` ever putting a street address in a
 * link.
 */
const toDetails = (grouping: CustomerRowGrouping) => ({
  name: grouping.name,
  accountType: grouping.accountType,
  addressLine1: grouping.addressLine1,
  addressLine2: grouping.addressLine2,
  city: grouping.city,
  provinceCode: grouping.provinceCode,
})

/** A drill row carrying the account details the customer table renders. */
type CustomerDrillRow = DrillRow & { details: ReturnType<typeof toDetails> }

const toRow = (grouping: CustomerRowGrouping) => ({
  // Both are the account's own: `hierarchyErp` is its Commercial-Partner code, null for a plain store,
  // and `sapErp` its SAP number. Neither is the banner it nests under — that is the endpoint's
  // `ancestorHierarchyErp`, which builds the tree and is deliberately not projected, since the page
  // neither shows it nor drills by it.
  grouping: { hierarchyErp: grouping.hierarchyErp, sapErp: grouping.sapErp },
  details: toDetails(grouping),
})

/**
 * The endpoint's rows as the drill table reads them: a parent per banner, each holding the accounts
 * beneath it.
 *
 * No `decodeDrillValue` anywhere: every row is one `fz_account` row, so both codes are real values or
 * genuinely absent, and the endpoint sends no sentinel to translate back.
 *
 * `labels` is not set. The name is not a code's label here — it is a column of its own, rendered with the
 * address beside it — so it travels in `details` with the rest of the account.
 */
function toCustomerDrillRows(data: CustomerVariance | undefined): CustomerDrillRow[] {
  if (!data) return []

  return data.results.map((row) => ({
    ...toRow(row.grouping),
    variance: row.variance,
    subRows: row.breakdown.map((entry) => ({
      ...toRow(entry.grouping),
      variance: entry.variance,
    })),
  }))
}

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  // `state: true` keeps the drill history (`drillFrom`) on the entry, as the header's own navigations do.

  const { conversion } = useCrtCurrencySync({
    search,
    navigate: (update) => navigate({ search: update, replace: true, state: true }),
  })

  // The comparison, the heat-map cell and the cost-type cell all come out of the search: the header
  // configured the two sides, the cell named the material scope, and the row the reader drilled named
  // which cost type and source these figures explain.
  const comparison = resolveComparison(search)
  const scope = toCostTypeCellScope(search)

  const { data, isPending, isPlaceholderData, error } = useCustomerVariance(comparison, scope)
  const rows = toCustomerDrillRows(data)

  /** Why the table has nothing to show, when it has nothing to show. */
  const unavailableError = !comparison
    ? new Error(t('varianceAnalysis.emptyState'))
    : !scope
      ? new Error(t('varianceAnalysis.drill.byBanner.unavailableScope'))
      : null

  /**
   * Where a row leads, asked of the same rule the endpoints refuse requests by.
   *
   * The filters are read off the **target** search rather than this page's — it is this page's plus the
   * row's own customer, so the rule sees both what the reader already narrowed to and what following the
   * link would add. A reader who arrived from `/by-agreement` carries a `masterAgreement`, which leaves
   * the agreement reading with nothing left to divide, and the row then offers nothing rather than a link
   * that comes back 422.
   */
  const targetsForRow = (row: DrillRow): DrillTarget[] => {
    // The Commercial-Partner code alone. An account number narrows nothing on the forecast side — no
    // forward-looking source records one — so sending it would promise a scope only half the comparison
    // could honour.
    const targetSearch = buildRowDrillSearch(search, row, ['hierarchyErp'])
    if (!targetSearch) return []

    return drillRoutesFor(toDrillFilters(targetSearch)).map((to) => ({
      to,
      search: targetSearch,
      label: t(TARGET_LABEL_KEYS[to]),
    }))
  }

  const leadingColumns: ColumnDef<CustomerDrillRow, unknown>[] = [
    {
      id: 'hierarchyErp',
      accessorFn: (row) => row.grouping.hierarchyErp ?? undefined,
      sortUndefined: 'last',
      header: ({ column }) => <TableHeader column={column} sortable />,
      meta: { displayTitle: t('varianceAnalysis.drill.table.hierarchyErp') },
      // Shown at every depth, because it is the account's **own** Commercial-Partner code rather than
      // the banner it nests under: a child carrying one is a banner in its own right, and a child
      // carrying none is a plain store. Both are worth seeing.
      cell: ({ row }) =>
        row.original.grouping.hierarchyErp ? (
          <CustomerId id={row.original.grouping.hierarchyErp} />
        ) : (
          <NoDataAvailable />
        ),
    },
    {
      id: 'sapErp',
      accessorFn: (row) => row.grouping.sapErp ?? undefined,
      sortUndefined: 'last',
      header: ({ column }) => <TableHeader column={column} sortable />,
      meta: { displayTitle: t('varianceAnalysis.drill.table.sapErp') },
      // Muted, so it reads as secondary to the banner code beside it. Every account has one; the only
      // row without is a banner code the account cache does not carry, which names no account at all.
      cell: ({ row }) =>
        row.original.grouping.sapErp ? (
          <MutedCustomerId id={row.original.grouping.sapErp} />
        ) : (
          <NoDataAvailable />
        ),
    },
    {
      id: 'accountType',
      accessorFn: (row) => row.details.accountType ?? undefined,
      sortUndefined: 'last',
      header: ({ column }) => <TableHeader column={column} sortable />,
      meta: { displayTitle: t('varianceAnalysis.drill.table.accountType') },
      cell: ({ row }) =>
        row.original.details.accountType ? (
          <Text tone="muted">{row.original.details.accountType}</Text>
        ) : (
          <NoDataAvailable />
        ),
    },
    {
      id: 'account',
      // Sorted on the name, which is what the reader is looking at; the address is the caption under it.
      accessorFn: (row) => row.details.name ?? undefined,
      sortUndefined: 'last',
      header: ({ column }) => <TableHeader column={column} sortable />,
      meta: { displayTitle: t('varianceAnalysis.drill.table.account') },
      cell: ({ row }) => {
        const { name, addressLine1, addressLine2, city, provinceCode } = row.original.details
        // An account the cache does not name has no address either — a gap in the account record rather
        // than a statement about the money, so the row still reports its figures.
        if (!name && !addressLine1 && !addressLine2 && !city && !provinceCode) {
          return <NoDataAvailable />
        }

        // Passed field by field rather than spread: the props are typed to the orders domain's customer,
        // and an object literal carrying `accountType` too would be rejected as an excess property.
        return (
          <CustomerNameAddress
            customer={{ name, addressLine1, addressLine2, city, provinceCode }}
          />
        )
      },
    },
  ]

  return (
    <PageContainer maxWidth="wide">
      <VStack gap="lg" align="stretch">
        <DrillPageHeader
          title={t('varianceAnalysis.drill.byBanner.pageHeader')}
          search={search}
          dimension={VARIANCE_GROUPING_DIMENSION_TYPES.byCustomer}
        />

        {/*
         * Nested where the source is recorded per account, flat where it is not: a manual program and a
         * key-account balance both stop at the banner, and the endpoint sends those parents with no
         * breakdown, so the expander simply does not appear.
         */}
        <DrillDataTable
          search={search}
          dimension={VARIANCE_GROUPING_DIMENSION_TYPES.byCustomer}
          rows={rows}
          leadingColumns={leadingColumns}
          getSubRows={(row) => row.subRows as CustomerDrillRow[] | undefined}
          canHideEmptyRows
          renderRowActions={(row) => (
            <DrillRowActions
              from={VARIANCE_GROUPING_DIMENSION_TYPES.byCustomer}
              targets={targetsForRow(row.original)}
            />
          )}
          currency={conversion}
          isPending={isPending}
          isRefreshing={isPlaceholderData}
          error={error ?? unavailableError}
          // Investment spend, so more of it is money going out: the increase reads as
          // unfavourable here, the same way it does on the cost-type table above.
          isIncreaseFavourable={false}
        />
      </VStack>
    </PageContainer>
  )
}
